import React from 'react';
import styles from './CandidateDetailsPanel.module.css';

import { formatMonthYear } from '../../utils/dataMappers';
import CandidateStatusBadge from './CandidateStatusBadge';

import { InterviewItem } from './types';
import { ATS_CONSTANTS } from './constants';
import { isCandidate, getAvailableActions } from './utils/atsUtils';
import { useCandidateProfile } from './hooks/useCandidateProfile';
import { useOfferPdf } from './hooks/useOfferPdf';

interface CandidateDetailsPanelProps {
  candidate: InterviewItem | null;
  jobStatus?: string;
  onClose: () => void;
  onReject?: (candidate: InterviewItem) => void;
  onAccept?: (candidate: InterviewItem) => void;
  onSchedule?: (candidate: InterviewItem) => void;
  onCancelInterview?: (candidate: InterviewItem) => void;
  onUpdateStatus?: (candidate: InterviewItem, status: string) => void;
  onReschedule?: (candidate: InterviewItem) => void;
  onGenerateOffer?: (candidate: InterviewItem) => void;
  showSkillMatch?: boolean;
}

const CandidateDetailsPanel: React.FC<CandidateDetailsPanelProps> = ({
  candidate,
  jobStatus = 'Active', // Default to Active if not provided
  onClose,
  onReject,
  onSchedule,
  onCancelInterview,
  onReschedule,
  onGenerateOffer,
  showSkillMatch = true,
}) => {
  // All hooks must be called before any conditional returns
  const { profile, loading: profileLoading, error: profileError } = useCandidateProfile(candidate?.candidate_id);
  const { downloadOfferPdf, downloading: pdfDownloading, pdfUrl, pdfLoading, pdfError } = useOfferPdf(
    candidate && isCandidate(candidate) && candidate.column === ATS_CONSTANTS.COLUMNS.ACCEPTED && candidate.offerLetter && candidate.offerLetter.file_path
      ? candidate.id
      : null
  );

  const handleDownloadOffer = async () => {
    if (candidate && isCandidate(candidate)) {
      await downloadOfferPdf(candidate.id);
    }
  };

  const isClosed = jobStatus === 'Closed' || jobStatus === 'Filled';

  // Early return after all hooks
  if (!candidate) return null;

  // Safe access to properties that might be missing in InterviewListItem
  const name = candidate.name;
  const column = isCandidate(candidate) ? candidate.column : undefined;
  const interviewDetails = candidate.interviewDetails;
  const offerLetter = isCandidate(candidate) ? candidate.offerLetter : undefined;
  const skillMatch = isCandidate(candidate) ? candidate.skillMatch : undefined;
  const matchingSkills = isCandidate(candidate) ? candidate.matchingSkills : undefined;

  const renderFooterActions = () => {
    // Hide all actions for closed jobs
    if (isClosed) {
      return null;
    }

    if (!candidate) return null;

    const actions = getAvailableActions(candidate);

    return (
      <>
        {actions.includes('REJECT') && onReject && (
          <button className={styles.buttonSecondary} onClick={() => onReject(candidate)} data-testid="candidate-details-reject-button">
            Reject
          </button>
        )}
        {actions.includes('SCHEDULE_INTERVIEW') && onSchedule && (
          <button className={styles.buttonPrimary} onClick={() => onSchedule(candidate)} data-testid="candidate-details-schedule-button">
            Schedule Interview
          </button>
        )}
        {actions.includes('GENERATE_OFFER') && onGenerateOffer && (
          <button className={styles.buttonPrimary} onClick={() => onGenerateOffer(candidate)} data-testid="candidate-details-generate-offer-button">
            Generate Offer
          </button>
        )}
        {actions.includes('RESCHEDULE') && onReschedule && (
          <button className={styles.buttonPrimary} onClick={() => onReschedule(candidate)} data-testid="candidate-details-reschedule-button">
            Reschedule
          </button>
        )}
        {actions.includes('CANCEL') && onCancelInterview && (
          <button className={styles.buttonCancel} onClick={() => onCancelInterview(candidate)} data-testid="candidate-details-cancel-button">
            Cancel Interview
          </button>
        )}
      </>
    );
  };

  return (
    <>
      <div className={styles.panelOverlay} onClick={onClose} data-testid="candidate-details-overlay" />
      <div className={styles.panel} data-testid="candidate-details-panel">
        <div className={styles.panelHeader}>
          <h2 className={styles.panelHeaderTitle} data-testid="candidate-details-name">{name}</h2>
          <button className={styles.panelHeaderClose} onClick={onClose} data-testid="candidate-details-close">
            ×
          </button>
        </div>
        <div className={styles.panelContent}>
          {column === ATS_CONSTANTS.COLUMNS.ACCEPTED && offerLetter && (
            <div className={styles.panelSection}>
              <h4 className={styles.panelSectionTitle} data-testid="candidate-details-offer-title">OFFER LETTER</h4>
              <div className={styles.resumeProfile}>
                <div className={styles.contactSection}>
                  <div className={styles.offerStatusBadge} data-testid="candidate-details-offer-status">
                    Status: {offerLetter.status}
                  </div>
                  {offerLetter.created_at && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel}>Generated:</span>
                      <span className={styles.contactValue}>{new Date(offerLetter.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {offerLetter.expires_at &&
                    offerLetter.status !== ATS_CONSTANTS.STATUS.EXPIRED &&
                    offerLetter.status !== ATS_CONSTANTS.STATUS.ACCEPTED && (
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Expires:</span>
                        <span className={styles.contactValue}>{new Date(offerLetter.expires_at).toLocaleDateString()}</span>
                      </div>
                    )}
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                {pdfLoading && <p>Loading PDF...</p>}
                {pdfError && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fee2e2',
                      borderRadius: '6px',
                      color: '#b91c1c',
                      fontSize: '14px',
                      marginTop: '8px',
                    }}
                  >
                    Error fetching offer letter
                  </div>
                )}
                {pdfUrl && !pdfLoading && !pdfError && (
                  <div>
                    <iframe
                      src={pdfUrl}
                      style={{
                        width: '100%',
                        height: '600px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                      }}
                      title="Offer Letter PDF"
                    />
                    <div style={{ marginTop: '12px' }}>
                      <button
                        onClick={handleDownloadOffer}
                        disabled={pdfDownloading}
                        className={styles.buttonPrimary}
                        data-testid="candidate-details-download-offer"
                      >
                        {pdfDownloading ? 'Downloading...' : 'Download PDF'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {interviewDetails && (
            <div className={styles.panelSection}>
              <h4 className={styles.panelSectionTitle} data-testid="candidate-details-interview-title">INTERVIEW DETAILS</h4>
              <div className={styles.resumeProfile}>
                <div className={styles.contactSection}>
                  <div className={styles.contactItem}>
                    <span className={styles.contactLabel}>Date:</span>
                    <span className={styles.contactValue}>{interviewDetails.date}</span>
                  </div>
                  <div className={styles.contactItem}>
                    <span className={styles.contactLabel}>Time:</span>
                    <span className={styles.contactValue}>{interviewDetails.time}</span>
                  </div>
                  <div className={styles.contactItem}>
                    <span className={styles.contactLabel}>Venue:</span>
                    <span className={styles.contactValue}>{interviewDetails.venue}</span>
                  </div>
                  <div className={styles.contactItem}>
                    <span className={styles.contactLabel}>Status:</span>
                    <span className={styles.contactValue}>
                      <div className={styles.interviewStatusContainer}>
                        <CandidateStatusBadge status={interviewDetails.status || ATS_CONSTANTS.STATUS.SCHEDULED} />
                      </div>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Personal Details Section - from full profile */}
          {profile && (
            <div className={styles.panelSection}>
              <h4 className={styles.panelSectionTitle} data-testid="candidate-details-personal-title">PERSONAL DETAILS</h4>
              <div className={styles.resumeProfile}>
                <div className={styles.contactSection}>
                  {profile.phone_masked && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel}>Phone:</span>
                      <span className={styles.contactValue}>{profile.phone_masked}</span>
                    </div>
                  )}
                  {profile.email_masked && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel}>Email:</span>
                      <span className={styles.contactValue}>{profile.email_masked}</span>
                    </div>
                  )}
                  {profile.dob && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel}>Date of Birth:</span>
                      <span className={styles.contactValue}>{profile.dob}</span>
                    </div>
                  )}
                  {profile.languages && profile.languages.length > 0 && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel}>Languages:</span>
                      <span className={styles.contactValue}>
                        {profile.languages.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Skill Match Section - from application data */}
          {showSkillMatch && (skillMatch !== undefined && skillMatch > 0 || (matchingSkills && matchingSkills.length > 0)) && (
            <div className={styles.panelSection}>
              <h4 className={styles.panelSectionTitle} data-testid="candidate-details-skill-title">AI SUMMARY</h4>
              <div className={styles.resumeProfile}>
                <div className={styles.contactSection}>
                  {skillMatch !== undefined && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel} data-testid="candidate-details-skill-match-score">Applicant Score: {skillMatch}%</span>
                    </div>
                  )}
                  {matchingSkills && matchingSkills.length > 0 && (
                    <div className={styles.contactItem} style={{ alignItems: 'flex-start' }}>
                      <span className={styles.contactValue}>
                        <div className={styles.skillsTags}>
                          {matchingSkills.map((skill: string, idx: number) => (
                            <span key={idx} className={styles.skillTag}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Full Profile Section - Education and Experience */}
          <div className={styles.panelSection}>
            <h4 className={styles.panelSectionTitle} data-testid="candidate-details-profile-title">DIGITAL PROFILE</h4>
            {profileLoading ? (
              <div className={styles.loadingState} data-testid="candidate-details-loading">Loading profile...</div>
            ) : profileError ? (
              <p className={styles.errorText}>Error loading profile: {profileError}</p>
            ) : profile ? (
              <div className={styles.resumeProfile}>
                {/* Education Section */}
                {profile.education && profile.education.length > 0 && (
                  <div className={styles.resumeSection}>
                    <h5 className={styles.resumeSectionTitle}>Education</h5>
                        {[...profile.education]
                          .sort((a, b) => (b.year || 0) - (a.year || 0))
                          .map((edu, idx: number) => (
                            <div key={idx} className={styles.resumeItem}>
                              <div className={styles.resumeItemHeader}>
                                <span className={styles.resumeItemTitle}>{edu.type}</span>
                                {edu.year && <span className={styles.resumeItemDate}>{edu.year}</span>}
                              </div>
                              <div className={styles.resumeItemInstitution}>{edu.institution}</div>
                              {edu.stream && (
                                <div className={styles.resumeItemDetails}>Stream: {edu.stream}</div>
                              )}
                              {edu.grade && (
                                <div className={styles.resumeItemDetails}>Grade: {edu.grade}</div>
                              )}
                              {edu.remarks && (
                                <div className={styles.resumeItemRemarks}>{edu.remarks}</div>
                              )}
                            </div>
                          ))}
                  </div>
                )}

                {/* Experience Section */}
                {profile.experience && profile.experience.length > 0 && (
                  <div className={styles.resumeSection}>
                    <h5 className={styles.resumeSectionTitle}>Experience</h5>
                        {[...profile.experience]
                          .sort((a, b) => {
                            const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
                            const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
                            return dateB - dateA;
                          })
                          .map((exp, idx: number) => (
                            <div key={idx} className={styles.resumeItem}>
                              <div className={styles.resumeItemHeader}>
                                <span className={styles.resumeItemTitle}>{exp.title}</span>
                                <span className={styles.resumeItemDate}>
                                  {formatMonthYear(exp.start_date)} -{' '}
                                  {exp.end_date ? formatMonthYear(exp.end_date) : 'Present'}
                                </span>
                              </div>
                              <div className={styles.resumeItemInstitution}>{exp.company_name}</div>
                              {exp.description && (
                                <div className={styles.resumeItemDescription}>{exp.description}</div>
                              )}
                            </div>
                          ))}
                  </div>
                )}

                    {/* Skills & Certifications Section */}
                    {profile.remarks && (
                      <div className={styles.resumeSection}>
                        <h5 className={styles.resumeSectionTitle}>Other Skills & Certifications</h5>
                        <div className={styles.remarksSection}>
                          <div className={styles.remarksContent}>{profile.remarks}</div>
                        </div>
                      </div>
                    )}
              </div>
            ) : (
              <div className={styles.noDataState}>No profile data available</div>
            )}
          </div>
        </div>
        <div className={styles.panelFooter}>{renderFooterActions()}</div>
      </div>
    </>
  );
};

export default CandidateDetailsPanel;
